import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Card,
  Button,
  Switch,
  Table,
  message,
  Modal
} from 'antd';
import { SyncOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { 
  getPaymentConfigurations,
  setDefaultPaymentConfiguration,
  deletePaymentConfiguration,
  syncPaymentConfigurations
} from '../../../api/Api';
import { useSelector } from 'react-redux';
import CustomDelete from '../../../../../assets/customSVG/CustomDelete';
import NotificationContext from '../../../../../providers/NotificationProvider';
import { showSuccessToast, showErrorToast } from '../../../../../components/commonComponents/gxAntdToast/toastUtils';
import YouTubeIcon from '../../../../../assets/customSVG/YouTubeIcon';
import { useFeature } from '../../../../../hooks/useFeatureGating';
import { FEATURES } from '../../../../../constant/featureSlugs';
import PremiumUpgradeScreen from '../../../../../components/commonComponents/PremiumUpgradeScreen/PremiumUpgradeScreen';

const { Title } = Typography;
const { confirm } = Modal;

const PaymentConfigurations = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentBrand } = useSelector((state) => state.app);

  const { isEnabled: isPaymentEnabled } = useFeature(FEATURES.PAYMENT_CONFIG);

  // notification(toast)
  const notification =  useContext(NotificationContext)

  // Fetch payment configurations on component mount
  useEffect(() => {
    fetchPaymentConfigurations();
  }, []);

  const fetchPaymentConfigurations = async () => {
    try {
      setLoading(true);
      const response = await getPaymentConfigurations(currentBrand?.brand_id);
      if (response?.status == 200) {
        // Transform API response to match our table structure
        const formattedData = response?.data?.payment_configurations?.map(item => ({
          key: item.id.toString(),
          id: item.id,
          paymentConfiguration: item.configuration_name,
          connection: item.payment_gateway,
          status: item.status,
          type: item.payment_gateway.toLowerCase(),
          isDefault: item.is_default
        }));
        setPaymentMethods(formattedData);
      }
    } catch (error) {
      message.error('Failed to fetch payment configurations');
      console.error('Error fetching payment configurations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      const response = await syncPaymentConfigurations(currentBrand?.brand_id);

      if (response?.status === 200) {
        // Refresh the payment configurations list
        await fetchPaymentConfigurations();
        showSuccessToast(notification, 'Payment configurations synced successfully');
      } else {
        showErrorToast(notification, response?.response?.data?.error || 'Failed to sync payment configurations');
      }
    } catch (error) {
      showErrorToast(notification, 'Failed to sync payment configurations');
      console.error('Sync error:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDefaultToggle = async (key) => {
    try {
      const method = paymentMethods.find(m => m.key === key);
      if (!method) return;

      const res = await setDefaultPaymentConfiguration(method.id, currentBrand?.brand_id);

      if (res?.status == 200) {
        // Update local state to reflect the change
        setPaymentMethods(prevMethods =>
          prevMethods.map(m => ({
            ...m,
            isDefault: m.key === key
        }))
        );
        showSuccessToast(notification, "Default payment configuration updated successfully");
      } else {
        showErrorToast(notification, res?.response?.data?.error || 'Failed to update default payment configuration');
      }
    } catch (error) {
      showErrorToast(notification, 'Failed to update default payment configuration');
    }
  };

  const handleDelete = (key) => {
    const method = paymentMethods.find(m => m.key === key);
    if (!method) return;

    confirm({
      title: 'Delete Payment Configuration',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete ${method.paymentConfiguration}?`,
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const res = await deletePaymentConfiguration(method.id, currentBrand?.brand_id);

          if (res?.status == 200) {
            setPaymentMethods(prev => prev.filter(m => m.key !== key));
            showSuccessToast(notification, "Payment configuration deleted successfully");
          } else {
            showErrorToast(notification, res?.response?.data?.error || 'Failed to delete payment configuration');
          }
        } catch (error) {
          showErrorToast(notification, 'Failed to delete payment configuration');
          console.error('Error deleting configuration:', error);
        }
      },
    });
  };

    const columns = [
      {
        title: 'Payment Configuration',
        dataIndex: 'paymentConfiguration',
        key: 'paymentConfiguration',
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* {record.type === 'zaakpay' && <img src="/zaakpay-logo.png" alt="ZaakPay" style={{ width: 24, height: 24 }} />}
            {record.type === 'razorpay' && <img src="/razorpay-logo.png" alt="Razorpay" style={{ width: 24, height: 24 }} />}
            {record.type === 'paypal' && <img src="/paypal-logo.png" alt="PayPal" style={{ width: 24, height: 24 }} />} */}
            <span>{text.toUpperCase()}</span>
          </div>
        ),
      },
      {
        title: 'Connection',
        dataIndex: 'connection',
        key: 'connection',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => (
          <div style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '12px',
            backgroundColor: status === 'ACTIVE' ? 'var(--primaryBackground)' : '#FFF5E6',
            color: status === 'ACTIVE' ? 'var(--primary)' : '#FF8C00',
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'capitalize'
          }}>
            {status}
          </div>
        ),
      },
      {
        title: 'Default',
        key: 'default',
        align: 'center',
        render: (_, record) => (
          <Switch
            checked={record.isDefault}
            onChange={() => handleDefaultToggle(record.key)}
            disabled={record.isDefault}
          />
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (_, record) => (
          <Button
            type="text"
            danger
            onClick={() => handleDelete(record.key)}
            disabled={record.isDefault}
          >
            <CustomDelete width="1.2vw" height="1.2vw" color="red" fillColors="red" />
          </Button>
        ),
      },
  ];


  return (
    <div className="payment-configurations-container flex flex-col gap-4">
      {/* New Card Section */}
      <Card className="border border-gray-200 rounded-lg shadow-sm">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            Setup Payment Configuration
          </h2>
          <p className="text-gray-600 mb-4">
            Gainwix.ai now helps you to capture payments directly on WhatsApp & track Orders on platform itself
          </p>
          <p className="text-gray-900 font-medium mb-4">
            Check how to setup payments on WhatsApp
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <a
              href="https://www.youtube.com/watch?v=SETUP_CONFIG_VIDEO_ID"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-center"
            >
              <div className="bg-[#E6F7EE] p-3 rounded-full mb-3">
                <YouTubeIcon stroke="#00A85A"/>
              </div>
              <span className="font-medium text-gray-800">1. Setup Payment Configuration</span>
            </a>

            <a
              href="https://www.youtube.com/watch?v=BROADCAST_VIDEO_ID"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-center"
            >
              <div className="bg-[#E6F7EE] p-3 rounded-full mb-3">
                <YouTubeIcon stroke="#00A85A"/>
              </div>
              <span className="font-medium text-gray-800">2. Payment in Broadcast</span>
            </a>

            <a
              href="https://www.youtube.com/watch?v=SEQUENCE_VIDEO_ID"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-center"
            >
              <div className="bg-[#E6F7EE] p-3 rounded-full mb-3">
                <YouTubeIcon stroke="#00A85A"/>
              </div>
              <span className="font-medium text-gray-800">3. Payment in Sequence</span>
            </a>
          </div>
        </div>
      </Card>

      {isPaymentEnabled ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <Title level={3} style={{ margin: 0 }}>Payment Configurations</Title>
            <Button
              type="primary"
              icon={<SyncOutlined spin={isSyncing} />}
              onClick={handleSync}
              loading={isSyncing}
              disabled={isSyncing}
              className="flex items-center bg-[--new-primary] hover:!bg-[--new-primary] text-white"
            >
              {isSyncing ? 'Syncing...' : 'Sync'}
            </Button>
          </div>

          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              borderRadius: '8px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
              border: '1px solid #F0F0F0',
              minHeight: '200px'
            }}
          >
            <Table
              columns={columns}
              dataSource={paymentMethods}
              pagination={false}
              loading={loading}
              rowKey="key"
              rowClassName={(record) => record.isDefault ? 'default-row' : ''}
              style={{ border: 'none' }}
              locale={{
                emptyText: loading ? 'Loading payment configurations...' : 'No payment configurations found'
              }}
            />
          </Card>
        </>
      ) : (
        <PremiumUpgradeScreen/>
      )}
    </div>
  );
};

export default PaymentConfigurations;
